import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Tooltip,
} from '@chakra-ui/react'
import { useField, useFormikContext } from 'formik'
import { isNull, isUndefined } from 'lodash'
import { useCallback, useMemo, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { CMFieldProps } from '.'
import { IKReactWindowedSelect } from '../../../../../ui-kit'
import { TypeBadge } from '../../../../../utils/mediaLibrary'

const CMSingleSelectField = ({
  cmField,
  disabled,
  isUpload,
}: CMFieldProps): ReactElement => {
  const { t } = useTranslation()

  const form = useFormikContext()

  const [field] = useField(`['${cmField.name}']`)

  const formattedValue = useMemo(() => {
    return (
      !(isUndefined(field.value) || isNull(field.value)) && {
        label: disabled || cmField.schema.readOnly ? (
          String(field.value)
        ) : (
          <>
            {String(field.value)} <TypeBadge val={field.value} />
          </>
        ),
        value: field.value,
      }
    )
  }, [field.value])

  const cmFieldSelectOptions = useMemo(
    () => cmField.schema.selectOptions ?? [],
    [cmField.schema.selectOptions],
  )

  const placeholder = useMemo(() => {
    if (
      !(
        isUndefined(cmField.schema.defaultValue) ||
        isNull(cmField.schema.defaultValue)
      ) &&
      isUpload
    ) {
      return `${cmField.schema.defaultValue}[${typeof cmField.schema
        .defaultValue}] (Default value)`
    }

    return ''
  }, [])

  const options = useMemo(
    () =>
      cmFieldSelectOptions?.map(option => ({
        label: String(option),
        value: option,
      })),
    [cmFieldSelectOptions],
  )

  const onChange = useCallback(
    (newVal: any) => {
      setTimeout(() => {
        form.setFieldTouched(cmField.name, true)
        if (isNull(newVal)) {
          form.setFieldValue(field.name, null)
        } else {
          form.setFieldValue(field.name, newVal.value)
        }
      }, 0)
    },
    [field.name, cmField.name],
  )

  return (
    <FormControl
      marginBottom={isUpload ? 0 : '30px'}
      isInvalid={Boolean(form.errors[cmField.name])}
      isRequired={cmField.schema.isValueRequired}
      isDisabled={disabled || cmField.schema.readOnly}
    >
      <Tooltip label={cmField.label} placement="top-start">
        <FormLabel isRequired={cmField.schema.isValueRequired}>
          {cmField.label}
        </FormLabel>
      </Tooltip>
      <Tooltip
        isDisabled={!cmField.schema.readOnly}
        label={t('read_only', 'Read only')}
        shouldWrapChildren
      >
        <IKReactWindowedSelect
          placeholder={placeholder}
          variant="compact"
          captureMenuScroll={false}
          menuPlacement="auto"
          isClearable
          isDisabled={disabled || cmField.schema.readOnly}
          value={formattedValue}
          options={options}
          onChange={onChange}
        />
      </Tooltip>
      <FormErrorMessage>{form.errors[cmField.name]}</FormErrorMessage>
    </FormControl>
  )
}

export default CMSingleSelectField
